import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ArrowRight } from 'lucide-react';
import { IndustriesDropdown } from './IndustriesDropdown';
import { OfferingsDropdown } from './OfferingsDropdown';
import { MobileMenu } from './MobileMenu';
import { useDropdownHover } from '../hooks/useDropdownHover';
import './GlobalHeader.css';

export const GlobalHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null); // 'industries' | 'offerings' | null
  const [isMobile, setIsMobile] = useState(false);
  const industriesNavItemRef = useRef(null);
  const offeringsNavItemRef = useRef(null);
  const location = useLocation();

  const {
    industriesOpen: isIndustriesDropdownOpen,
    setIndustriesOpen: setIsIndustriesDropdownOpen,
    offeringsOpen: isOfferingsDropdownOpen,
    setOfferingsOpen: setIsOfferingsDropdownOpen,
    openIndustries: handleIndustriesEnter,
    closeIndustries: handleIndustriesLeave,
    openOfferings: handleOfferingsEnter,
    closeOfferings: handleOfferingsLeave,
    cancelCloseOnDropdownEnter: handleDropdownMouseEnter,
    scheduleCloseOnDropdownLeave: handleDropdownMouseLeave,
  } = useDropdownHover(isMobile);

  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setIsIndustriesDropdownOpen(false);
    setIsOfferingsDropdownOpen(false);
    setIsMenuOpen(false);
    setMobileExpanded(null);
  }, [location.pathname]);

  // Body scroll lock when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.add('nav-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('nav-open');
      document.body.style.overflow = '';
    }
    return () => {
      document.body.classList.remove('nav-open');
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  // ESC to close
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key !== 'Escape') return;
      if (isIndustriesDropdownOpen) setIsIndustriesDropdownOpen(false);
      else if (isOfferingsDropdownOpen) setIsOfferingsDropdownOpen(false);
      else if (isMenuOpen) setIsMenuOpen(false);
    };
    if (isMenuOpen || isIndustriesDropdownOpen || isOfferingsDropdownOpen) {
      document.addEventListener('keydown', handleEsc);
    }
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isMenuOpen, isIndustriesDropdownOpen, isOfferingsDropdownOpen]);

  // Hover handlers come from useDropdownHover hook above.

  const isTransparent = isHomePage && !isScrolled;
  const logoSrc = isTransparent ? '/logos/aplied-logo-white.png' : '/logos/aplied-logo-dark.png';

  return (
    <header className={`global-header ${isTransparent ? 'transparent' : 'solid'} ${isScrolled ? 'scrolled' : ''}`}>
      <div className="global-header__container">
        <Link to="/" className="global-header__logo">
          <img src={logoSrc} alt="APLIED.AI" className="global-header__logo-img" />
        </Link>

        {/* Desktop Navigation — 4 items + sticky Book CTA */}
        <nav className="global-header__nav desktop-nav" data-testid="desktop-nav">
          {/* Industries (dropdown-only trigger — no clickable page) */}
          <div
            className="global-header__nav-item"
            ref={industriesNavItemRef}
            onMouseEnter={handleIndustriesEnter}
            onMouseLeave={handleIndustriesLeave}
          >
            <button
              type="button"
              className={`global-header__nav-link global-header__nav-link--primary global-header__nav-trigger ${location.pathname.startsWith('/industries') || location.pathname === '/mining' || location.pathname === '/industrials' ? 'active' : ''}`}
              aria-haspopup="true"
              aria-expanded={isIndustriesDropdownOpen}
              onClick={() => setIsIndustriesDropdownOpen((v) => !v)}
              data-testid="nav-industries"
            >
              Industries
              <ChevronDown size={14} className={`global-header__chevron-inline ${isIndustriesDropdownOpen ? 'rotated' : ''}`} />
            </button>
            <IndustriesDropdown
              isOpen={isIndustriesDropdownOpen}
              onClose={() => setIsIndustriesDropdownOpen(false)}
              isMobile={isMobile}
              onMouseEnter={handleDropdownMouseEnter}
              onMouseLeave={() => handleDropdownMouseLeave(setIsIndustriesDropdownOpen)}
            />
          </div>

          {/* Offerings (with mega dropdown) */}
          <div
            className="global-header__nav-item"
            ref={offeringsNavItemRef}
            onMouseEnter={handleOfferingsEnter}
            onMouseLeave={handleOfferingsLeave}
          >
            <Link
              to="/offerings"
              className={`global-header__nav-link global-header__nav-link--primary ${location.pathname === '/offerings' ? 'active' : ''}`}
              aria-haspopup="true"
              aria-expanded={isOfferingsDropdownOpen}
              data-testid="nav-offerings"
            >
              Offerings
              <ChevronDown size={14} className={`global-header__chevron-inline ${isOfferingsDropdownOpen ? 'rotated' : ''}`} />
            </Link>
            <OfferingsDropdown
              isOpen={isOfferingsDropdownOpen}
              onClose={() => setIsOfferingsDropdownOpen(false)}
              isMobile={isMobile}
              onMouseEnter={handleDropdownMouseEnter}
              onMouseLeave={() => handleDropdownMouseLeave(setIsOfferingsDropdownOpen)}
            />
          </div>

          <Link
            to="/capabilities"
            className={`global-header__nav-link ${location.pathname === '/capabilities' ? 'active' : ''}`}
            data-testid="nav-capabilities"
          >
            Capabilities
          </Link>

          <Link
            to="/about"
            className={`global-header__nav-link ${location.pathname === '/about' ? 'active' : ''}`}
            data-testid="nav-about"
          >
            About
          </Link>

          {/* Sticky Book a meeting CTA — pill button */}
          <Link
            to="/book-a-meeting"
            className="global-header__cta-pill"
            data-testid="nav-book-meeting"
          >
            Book a meeting
            <ArrowRight size={16} />
          </Link>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="global-header__mobile-btn"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
          aria-expanded={isMenuOpen}
          aria-controls="primary-nav"
          data-testid="mobile-menu-btn"
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Full-Screen Menu — accordion-style */}
      {isMenuOpen && (
        <MobileMenu
          expanded={mobileExpanded}
          setExpanded={setMobileExpanded}
          onClose={() => setIsMenuOpen(false)}
        />
      )}
    </header>
  );
};
